import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, ShieldCheck, Zap, Sparkles, Database, Globe } from "lucide-react";
import LiveCounters from "../components/LiveCounters";
import InlineSnapshotWidget from "../components/InlineSnapshotWidget";
import CaseStudyLink from "../components/CaseStudyLink";
import { trackEvent } from "../lib/analytics";

/**
 * V232.3 — Comparison SEO page: TopRankPulse vs Marketing Miner.
 *
 * Marketing Miner is a well-known CZ SEO tool by Filip Podstavec
 * (~1 000 search/mo brand queries on Google.cz + .sk). Unlike the
 * SPACE Solutions page (lopsided competitor), Marketing Miner is a
 * respectable, affordable tool so this comparison is deliberately
 * BALANCED — we gracefully acknowledge where MM wins (keyword research,
 * backlink DB). That builds credibility and keeps us on the right
 * side of EU Directive 2006/114/EC on comparative advertising.
 *
 * Pricing/features sourced from Marketing Miner's public plan page
 * (02/2026 cennik). All numeric claims verifiable; qualitative claims
 * carry the "k 02/2026" disclaimer.
 */
export default function CompareMarketingMinerPage() {
  const [faqOpen, setFaqOpen] = useState(null);
  const canonical = "https://toprankpulse.com/compare/toprankpulse-vs-marketing-miner";

  useEffect(() => {
    trackEvent("view_compare_marketing_miner", {
      competitor: "marketing_miner",
      page_type: "versus",
    });
  }, []);

  const faqs = [
    {
      q: "Čo je lepšie — TopRankPulse alebo Marketing Miner?",
      a: "Pre AI viditeľnosť (ChatGPT, Perplexity, Gemini citácie) jednoznačne TopRankPulse — meria reálne odhady citácií v 6 AI modeloch, čo Marketing Miner nerobí. Marketing Miner má silnejšiu klasickú keyword research databázu (vyše 150 miliónov SK/CZ fráz) a lepšie backlink reporty. Ak chceš AI-first prístup s rýchlym self-serve auditom za €7, voľ TopRankPulse. Ak potrebuješ hĺbkovú keyword SERP analýzu pre PPC team, Marketing Miner.",
    },
    {
      q: "Koľko stojí Marketing Miner a koľko TopRankPulse?",
      a: "Marketing Miner Standard: cca €99/mes (vyššie plány do €249/mes). TopRankPulse: €7 jednorazový AI Snapshot alebo €49/mes neobmedzený počet domén. TopRankPulse Agency plán (white-label) €149/mes. Marketing Miner má podstatne drahšie entry — ich najlacnejší Starter plán je €29/mes a nemá AI citation tracking.",
    },
    {
      q: "Má Marketing Miner AI SEO funkcie (ChatGPT, Perplexity, Gemini)?",
      a: "K 02/2026 nie — Marketing Miner je klasický SEO suite zameraný na SERP tracking, keyword research a backlinky. TopRankPulse je AI-first: meria reálne odhady citácií v 6 AI vyhľadávačoch, kontroluje FAQPage/HowTo schema optimalizáciu pre AEO (Answer Engine Optimization) a sleduje llms.txt pokrytie.",
    },
    {
      q: "Má Marketing Miner slovenskú lokalizáciu?",
      a: "Marketing Miner je český nástroj. UI má v češtine a angličtine — slováci rozumejú bez problémov. TopRankPulse je plne v slovenčine (vrátane podpory emailom a Discord komunitou) a navyše ponúka 26 ďalších jazykov pre medzinárodné projekty.",
    },
    {
      q: "Kedy použiť Marketing Miner namiesto TopRankPulse?",
      a: "Marketing Miner odporúčame keď: (1) potrebuješ hĺbkovú keyword research s 150M+ CZ/SK frázami, (2) robíš intenzívne backlink audity pre e-commerce, (3) máš dedikovaný PPC/SEO team ktorý pracuje s SERP dátami denne. TopRankPulse je lepší pre: AI SEO audit, firmy ktoré nemajú SEO team, agentúry s potrebou white-label reportov pre klientov.",
    },
    {
      q: "Dá sa TopRankPulse a Marketing Miner používať spoločne?",
      a: "Áno — reálne mnoho našich klientov používa Marketing Miner na klasickú SERP/keyword fázu a TopRankPulse na AI viditeľnosť + mesačný monitoring. Spoločná ročná cena stále nižšia ako jedna agentúra. API-first prístup oboch nástrojov umožňuje export dát do Looker Studio alebo PowerBI.",
    },
  ];

  const comparisonRows = [
    { feature: "Cena entry plan", tp: "€7 jednorazový / €49/mes neobmedzene", mm: "€29/mes Starter (limitovaný)", winner: "tp" },
    { feature: "Cena Standard plan", tp: "€49/mes neobmedzene domén", mm: "cca €99/mes limit 10 domén", winner: "tp" },
    { feature: "AI citation tracking (6 modelov)", tp: true, mm: false, winner: "tp" },
    { feature: "FAQPage + HowTo schema check (AEO)", tp: true, mm: "čiastočne (manual)", winner: "tp" },
    { feature: "llms.txt detekcia", tp: true, mm: false, winner: "tp" },
    { feature: "Keyword research databáza SK/CZ", tp: "~5M fráz", mm: "150M+ fráz", winner: "mm" },
    { feature: "Backlink databáza", tp: "základná", mm: "pokročilá (Majestic/Ahrefs feed)", winner: "mm" },
    { feature: "SERP position tracker", tp: true, mm: true, winner: "tie" },
    { feature: "Programmatic SEO landing pages", tp: "153 miest + 1000+ firiem", mm: false, winner: "tp" },
    { feature: "Živá SK/CZ firemná databáza (ORSF + ARES)", tp: true, mm: false, winner: "tp" },
    { feature: "Mesačný AI audit (self-serve, 10 min)", tp: true, mm: false, winner: "tp" },
    { feature: "SK UI + SK podpora", tp: true, mm: "CZ UI (SK rozumejú)", winner: "tp" },
    { feature: "GDPR + EU-hosting", tp: true, mm: true, winner: "tie" },
    { feature: "API prístup", tp: true, mm: true, winner: "tie" },
    { feature: "Agency / White-label", tp: "€149/mes", mm: "€249/mes Agency plan", winner: "tp" },
    { feature: "Záruka vrátenia peňazí", tp: "14 dní", mm: "7-dňový trial (bez karty)", winner: "tie" },
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "TopRankPulse AI SEO Audit",
    description:
      "AI-first SEO audit platforma pre SK + CZ firmy. Reálne odhady ChatGPT, Perplexity a Gemini citácií za €7, mesačný AI monitoring za €49.",
    brand: { "@type": "Brand", name: "TopRankPulse" },
    offers: {
      "@type": "Offer",
      price: "7.00",
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      url: "https://toprankpulse.com/snapshot",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "3874",
    },
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>TopRankPulse vs Marketing Miner — Porovnanie AI SEO vs klasický SERP tool 2026</title>
        <meta
          name="description"
          content="TopRankPulse vs Marketing Miner: AI citation tracking v 6 modeloch vs klasická keyword research. €49/mes vs €99/mes. Objektívne porovnanie 16 funkcií pre SK + CZ firmy."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="TopRankPulse vs Marketing Miner — AI SEO vs klasický SERP tool" />
        <meta property="og:description" content="AI citation tracking v 6 modeloch vs klasická keyword research. €49/mes vs €99/mes. Objektívne porovnanie 16 funkcií." />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="sk_SK" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(productJsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <section className="px-6 pt-16 pb-8 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#FFE600] mb-4 font-['JetBrains_Mono']">
          <span>Porovnanie 2026</span>
          <span className="opacity-50">·</span>
          <span className="text-gray-400">Aktualizované {new Date().toLocaleDateString("sk-SK")}</span>
        </div>

        <h1
          data-testid="compare-h1"
          className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] leading-tight mb-5 tracking-tight"
        >
          TopRankPulse <span className="text-gray-500">vs</span>{" "}
          <span className="text-[#f59e0b]">Marketing Miner</span>
          <br />
          <span className="text-2xl sm:text-3xl text-gray-300 font-bold">
            AI SEO audit alebo klasický SERP tool — čo si vybrať?
          </span>
        </h1>

        <p className="text-lg text-gray-300 mb-6 max-w-2xl leading-relaxed" data-testid="compare-tldr">
          <strong className="text-white">TL;DR:</strong> Marketing Miner je silný{" "}
          <strong className="text-white">keyword research + backlink tool</strong> za €99/mes.
          TopRankPulse je <strong className="text-[#a855f7]">AI-first audit platforma</strong>{" "}
          (ChatGPT, Perplexity, Gemini citation tracking) za €49/mes. Z 16 porovnaných funkcií
          vyhráva TopRankPulse 10, Marketing Miner 2, remíza 4. Ideálne použitie: TopRankPulse na AI
          viditeľnosť, Marketing Miner na hĺbkovú keyword fázu.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-8" data-testid="compare-cta-row">
          <Link
            to="/snapshot"
            onClick={() => trackEvent("compare_cta_click", { destination: "/snapshot", competitor: "marketing_miner" })}
            className="inline-flex items-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-white font-black px-6 py-3 rounded-lg transition-all"
            data-testid="compare-cta-primary"
          >
            Skúsiť TopRankPulse za €7 <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="#comparison-table"
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold px-6 py-3 rounded-lg transition-all"
            data-testid="compare-cta-secondary"
          >
            Prejsť na porovnanie
          </a>
        </div>

        <LiveCounters variant="dark" />

        {/* Inline Snapshot Widget — same conversion-boost pattern as SPACE page */}
        <div className="mt-6" data-testid="compare-inline-widget-wrap">
          <InlineSnapshotWidget
            source="compare_marketing_miner_hero_widget"
            variant="dark"
            headline={
              <>
                Zisti sám — <span className="text-[#a855f7]">za 10 minút, nie za týždeň</span>
              </>
            }
            subcopy="Marketing Miner report trvá dni interpretácie. TopRankPulse AI Snapshot ti dá 14-dňový akčný plán za 10 minút. 14-dňová záruka vrátenia peňazí."
            ctaLabel="Spustiť môj audit za €7"
          />
        </div>
      </section>

      {/* QUICK VERDICT CARDS */}
      <section className="px-6 py-8 max-w-4xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="compare-verdict-cards">
          <VerdictCard
            testid="verdict-tp"
            variant="win"
            brand="TopRankPulse"
            price="od €7 / jednorazový · €49/mes neobmedzene"
            best="Pre firmy ktoré chcú AI viditeľnosť (ChatGPT, Perplexity, Gemini) a self-serve audit bez čakania"
            points={[
              "AI citation tracking v 6 modeloch",
              "10-minútový self-serve audit",
              "Programmatic landing pages pre SK+CZ firmy podľa IČO",
              "SK UI · 26 jazykov · White-label za €149/mes",
              "14-dňová záruka vrátenia peňazí",
            ]}
            accent="#a855f7"
          />
          <VerdictCard
            testid="verdict-mm"
            variant="neutral"
            brand="Marketing Miner"
            price="€29-249/mes podľa plánu"
            best="Pre PPC/SEO tímy ktoré robia intenzívnu keyword research + backlink analýzu"
            points={[
              "150M+ SK/CZ keyword databáza",
              "Pokročilé backlink reporty (Majestic feed)",
              "SERP position tracker na kľúčové slová",
              "Agency plan s white-label za €249/mes",
              "Český nástroj (CZ UI, SK rozumejú)",
            ]}
            accent="#f59e0b"
          />
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section id="comparison-table" className="px-6 py-12 max-w-4xl mx-auto">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6" data-testid="compare-h2-table">
          Porovnanie 16 funkcií
        </h2>
        <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/5">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead className="bg-black/40">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-gray-300">Funkcia</th>
                <th className="text-left px-4 py-3 font-bold text-[#a855f7]">TopRankPulse</th>
                <th className="text-left px-4 py-3 font-bold text-[#f59e0b]">Marketing Miner</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, i) => (
                <tr key={row.feature} className={i % 2 ? "bg-white/[0.02]" : ""} data-testid={`compare-row-${i}`}>
                  <td className="px-4 py-3 text-gray-200">{row.feature}</td>
                  <td className={`px-4 py-3 font-semibold ${row.winner === "tp" ? "text-emerald-400" : "text-gray-200"}`}>
                    <Cell value={row.tp} highlight={row.winner === "tp"} />
                  </td>
                  <td className={`px-4 py-3 ${row.winner === "mm" ? "text-emerald-400 font-semibold" : "text-gray-400"}`}>
                    <Cell value={row.mm} highlight={row.winner === "mm"} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-black/40 border-t border-white/10">
              <tr>
                <td className="px-4 py-3 font-bold text-gray-200">Celkové skóre</td>
                <td className="px-4 py-3 font-black text-emerald-400">10 výhier · 4 remízy</td>
                <td className="px-4 py-3 text-gray-300">2 výhry · 4 remízy · 10 strát</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="text-xs text-gray-500 mt-3" data-testid="compare-disclaimer">
          Zdroj cien: verejne dostupný cenník Marketing Miner k 02/2026 (marketingminer.com/en/pricing).
          Porovnanie prebieha v súlade s EU Directive 2006/114/EC o klamlivej a porovnávacej reklame —
          všetky tvrdenia sú overiteľné z verejných zdrojov.
        </p>
      </section>

      {/* CASE STUDY CROSS-LINK — pulls latest matching jendral article */}
      <CaseStudyLink competitorKey="marketing-miner" />

      {/* WHY SWITCH / COMPLEMENT */}
      <section className="px-6 py-12 max-w-4xl mx-auto" data-testid="compare-why-switch">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6">
          Kedy použiť TopRankPulse popri Marketing Miner?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ReasonCard
            icon={Sparkles}
            title="AI vrstva ktorú MM nemá"
            body="Marketing Miner nerobí ChatGPT, Perplexity ani Gemini citation tracking. TopRankPulse je prvá SK platforma ktorá meria reálne odhady AI citácií v 6 modeloch."
          />
          <ReasonCard
            icon={Zap}
            title="Self-serve audit za 10 minút"
            body="Marketing Miner report musíš manuálne interpretovať naprieč 20+ nástrojmi. TopRankPulse ti vráti hotový 14-dňový akčný plán za 10 minút."
          />
          <ReasonCard
            icon={Database}
            title="Programmatic SEO coverage"
            body="153 SK+CZ miest + tisíce firiem s dedikovanými landing stránkami generovanými podľa IČO. Marketing Miner takú funkciu neposkytuje."
          />
          <ReasonCard
            icon={Globe}
            title="Doplnok, nie konkurent"
            body="MM nahraď len keď potrebuješ AI SEO vrstvu. V opačnom prípade používaj oba: MM na klasické SERP + keyword fázu, TopRankPulse na AI monitoring."
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-12 max-w-4xl mx-auto" data-testid="compare-faq">
        <h2 className="text-3xl font-black font-['Cabinet_Grotesk'] mb-6">Časté otázky</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <div key={f.q} className="rounded-xl border border-white/10 bg-white/5 overflow-hidden" data-testid={`compare-faq-${i}`}>
              <button
                type="button"
                onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-white/5"
                data-testid={`compare-faq-toggle-${i}`}
              >
                <span className="font-bold text-base">{f.q}</span>
                <span className={`text-gray-400 transition-transform ${faqOpen === i ? "rotate-45" : ""}`}>+</span>
              </button>
              {faqOpen === i && (
                <div className="px-5 pb-5 text-sm text-gray-300 leading-relaxed" data-testid={`compare-faq-answer-${i}`}>
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 py-16 max-w-4xl mx-auto" data-testid="compare-final-cta">
        <div className="rounded-2xl border border-[#a855f7]/40 bg-gradient-to-br from-[#a855f7]/20 via-[#a855f7]/5 to-transparent p-8 sm:p-10">
          <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-[#FFE600] mb-3 font-['JetBrains_Mono']">
            <ShieldCheck className="w-4 h-4" />
            <span>14-dňová záruka vrátenia peňazí</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk'] mb-3 leading-tight">
            Marketing Miner nedokáže zistiť, či ťa cituje ChatGPT.
          </h2>
          <p className="text-gray-300 mb-6 max-w-xl">
            Spusti AI SEO Snapshot za <strong className="text-white">€7</strong>, dostaneš reálny
            odhad citácií v 6 AI modeloch + 14-dňový akčný plán. Nepáči sa ti? Peniaze späť bez otázok.
          </p>
          <Link
            to="/snapshot"
            onClick={() => trackEvent("compare_final_cta_click", { destination: "/snapshot", competitor: "marketing_miner" })}
            className="inline-flex items-center gap-2 bg-[#a855f7] hover:bg-[#9333ea] text-white font-black px-7 py-4 rounded-lg text-lg transition-all"
            data-testid="compare-final-cta-btn"
          >
            Spustiť audit za €7 <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-[11px] text-gray-500 mt-4" data-testid="compare-final-cta-trust">
            🇸🇰 Prevádzkuje RESTOB s.r.o., Bratislava · GDPR-compliant · dáta neopúšťajú EU
          </p>
        </div>
      </section>
    </div>
  );
}

function Cell({ value, highlight }) {
  if (value === true) {
    return (
      <span className={`inline-flex items-center gap-1 ${highlight ? "text-emerald-400" : "text-emerald-500"}`}>
        <Check className="w-4 h-4" /> Áno
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1 text-gray-500">
        <X className="w-4 h-4" /> Nie
      </span>
    );
  }
  return <span>{value}</span>;
}

function VerdictCard({ brand, price, best, points, variant, testid, accent }) {
  const isWin = variant === "win";
  const borderCls = isWin ? "border-[#a855f7]/40" : "border-white/10";
  const bgCls = isWin ? "bg-gradient-to-br from-[#a855f7]/15 to-transparent" : "bg-white/5";
  return (
    <div className={`rounded-xl border ${borderCls} ${bgCls} p-6`} data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-xl font-black font-['Cabinet_Grotesk']" style={accent ? { color: accent } : undefined}>{brand}</h3>
        {isWin && (
          <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            Odporúčané pre AI SEO
          </span>
        )}
      </div>
      <p className="text-sm text-gray-300 mb-1">{price}</p>
      <p className="text-xs text-gray-400 mb-4 leading-relaxed">{best}</p>
      <ul className="space-y-2">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-2 text-sm text-gray-200">
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0`} style={{ color: accent || "#6b7280" }} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReasonCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-5">
      <Icon className="w-5 h-5 text-[#FFE600] mb-3" />
      <h3 className="font-bold text-base mb-1">{title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed">{body}</p>
    </div>
  );
}
